import { API_CONFIG } from '../config/api';
import { ApiValidationError } from '../types/auth';
import type {
  CreateInventoryAdjustmentData,
  InventoryAdjustment,
  InventoryAdjustmentsParams,
  InventoryAdjustmentsResponse,
  InventoryAdjustmentsResult,
  InventoryAdjustmentResponse,
  VoidInventoryAdjustmentData,
} from '../types/inventoryAdjustment';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parseErrorResponse = async (response: Response, fallback: string) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

export const getInventoryAdjustments = async (
  params?: InventoryAdjustmentsParams
): Promise<InventoryAdjustmentsResult> => {
  const queryParams = new URLSearchParams();

  if (params?.start_date) {
    queryParams.set('start_date', params.start_date);
  }
  if (params?.end_date) {
    queryParams.set('end_date', params.end_date);
  }
  if (params?.status) {
    queryParams.set('status', params.status);
  }
  if (params?.per_page) {
    queryParams.set('per_page', String(params.per_page));
  }
  if (params?.page) {
    queryParams.set('page', String(params.page));
  }

  const query = queryParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVENTORY_ADJUSTMENTS.GET_ALL}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch inventory adjustments');
  }

  const result: InventoryAdjustmentsResponse = await response.json();
  return {
    adjustments: result.data,
    meta: result.meta,
    links: result.links,
  };
};

export const getInventoryAdjustmentById = async (id: number): Promise<InventoryAdjustment> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVENTORY_ADJUSTMENTS.GET_BY_ID}/${id}`,
    {
      method: 'GET',
      headers: authHeaders(),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch inventory adjustment');
  }

  const result: InventoryAdjustmentResponse = await response.json();
  return result.data;
};

export const createInventoryAdjustment = async (
  data: CreateInventoryAdjustmentData
): Promise<InventoryAdjustment> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVENTORY_ADJUSTMENTS.CREATE}`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to create inventory adjustment');
  }

  const result: InventoryAdjustmentResponse = await response.json();
  return result.data;
};

export const voidInventoryAdjustment = async (
  id: number,
  data?: VoidInventoryAdjustmentData
): Promise<InventoryAdjustment> => {
  const trimmedReason = data?.void_reason?.trim();
  const body = trimmedReason ? JSON.stringify({ void_reason: trimmedReason }) : undefined;

  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INVENTORY_ADJUSTMENTS.VOID}/${id}/void`,
    {
      method: 'POST',
      headers: {
        ...authHeaders(),
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(body ? { body } : {}),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to void inventory adjustment');
  }

  const result: InventoryAdjustmentResponse = await response.json();
  return result.data;
};
